import type { WorkBook, WorkSheet } from 'xlsx'
import * as XLSX from 'xlsx'

import { findFirstHeaderRowIndex, STRUCTURED_TABLE_MIN_NONEMPTY_COLUMNS } from './add-file-mapping/utils'

export type PickFirstViableExcelSheetOptions = {
  /** Same `raw` flag used in `sheet_to_json` for this workbook (false for native Excel). */
  raw: boolean
  /**
   * Minimum number of non-empty cells on the first *structural* table row (same rule as file-mapping preview).
   * @default {@link STRUCTURED_TABLE_MIN_NONEMPTY_COLUMNS}
   */
  minNonEmptyFirstRowCells?: number
  /**
   * Minimum row count from the structural start (e.g. header + at least one data row).
   * @default 2
   */
  minDataRows?: number
}

const countNonEmptyCellsInRow = (row: unknown[] | undefined): number => {
  if (!row?.length) return 0
  return row.filter((cell) => {
    if (cell === null || cell === undefined) return false
    return String(cell).replace(/\s+/g, '').length > 0
  }).length
}

/**
 * SheetJS derives the column origin from the sheet's `!ref`, so columns that were never written at
 * the start of the sheet are not represented at all: a file whose data starts at column B yields
 * `!ref = "B1:D3"`, making array index 0 the physical column B. The file-processing engine counts
 * from the first physical column instead, so a mapping saved from the preview would point one
 * column to the left of the intended one. Rewriting the range to start at column A keeps both
 * readings aligned - sheets that already start at column A are returned untouched.
 */
const normalizeRangeToFirstPhysicalColumn = (worksheet: WorkSheet): WorkSheet => {
  const ref = worksheet?.['!ref']
  if (!ref) return worksheet

  const range = XLSX.utils.decode_range(ref)
  if (range.s.c === 0) return worksheet

  return { ...worksheet, '!ref': XLSX.utils.encode_range({ ...range, s: { ...range.s, c: 0 } }) }
}

/**
 * Returns the first worksheet (in workbook order) that contains a structural table region
 * (same detection as file mapping: skip cover/preamble rows; require enough columns and depth).
 * Skips empty or “info-only” sheets where row 0 is sparse but the real grid starts later (or on another tab).
 * If none qualify, falls back to the first sheet (legacy behavior).
 */
export function pickFirstViableExcelSheet(
  workbook: WorkBook,
  options: PickFirstViableExcelSheetOptions,
): { sheetName: string; worksheet: WorkSheet } {
  const minCells = options.minNonEmptyFirstRowCells ?? STRUCTURED_TABLE_MIN_NONEMPTY_COLUMNS
  const minRows = options.minDataRows ?? 2
  const { raw } = options

  for (const sheetName of workbook.SheetNames) {
    const worksheet = normalizeRangeToFirstPhysicalColumn(workbook.Sheets[sheetName])
    if (!worksheet) continue

    const listData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      blankrows: true,
      range: 0,
      defval: '',
      raw,
    }) as unknown[][]

    if (!listData?.length) continue

    const structuralStart = findFirstHeaderRowIndex(listData, true)
    const depthFromStart = listData.length - structuralStart
    if (depthFromStart < minRows) continue

    if (countNonEmptyCellsInRow(listData[structuralStart] as unknown[]) >= minCells) {
      return { sheetName, worksheet }
    }
  }

  const sheetName = workbook.SheetNames[0]
  return { sheetName, worksheet: normalizeRangeToFirstPhysicalColumn(workbook.Sheets[sheetName]) }
}
